/**
 * @fileoverview Shared CSS serializers for band/element styling — the single source
 * for turning a BandStyle / ElementStyle / BorderStyle into an inline CSS string.
 * @module document-render/bandStyle
 *
 * Responsibilities: - Serialize BandStyle (background/border/padding/columns) to CSS.
 *                   - Serialize ElementStyle (font/color/highlight/align) to CSS.
 *                   - Serialize BorderStyle to a CSS border shorthand.
 * Dependencies:     - ./documentLayout (BandStyle, ElementStyle, BorderStyle types).
 * Architecture:     - Pure functions, no I/O. Imported by both renderDocumentLayout
 *                     (layout-only path) and renderSections (data-driven path) so the
 *                     two rendering surfaces apply band styling identically (G-RENDER-BG-01).
 * Notes:            - Extracted from renderDocumentLayout to avoid a circular import once
 *                     renderSections needed band styling for the data-driven band wrapper.
 */

import type { BandStyle, ElementStyle, BorderStyle } from './documentLayout';

/**
 * @process borderCss
 * Steps: Serialize a BorderStyle to the CSS border shorthand (+ optional radius).
 * Inputs: BorderStyle.
 * Outputs: CSS string. Deterministic.
 * Idempotency: REQUIRED (pure).
 */
export function borderCss(b: BorderStyle): string {
  const radius = b.radius ? `border-radius:${b.radius}pt;` : '';
  return `${b.width}pt ${b.style} ${b.color};${radius ? ' ' + radius : ''}`;
}

/**
 * @process elementStyleCss
 * Steps: Serialize an ElementStyle (font/size/color/highlight/weight/align/lineHeight) to CSS.
 * Inputs: ElementStyle.
 * Outputs: CSS string. Deterministic.
 * Idempotency: REQUIRED (pure).
 */
export function elementStyleCss(s: ElementStyle): string {
  const parts: string[] = [];
  if (s.fontFamily) parts.push(`font-family:${s.fontFamily}`);
  if (s.fontSize) parts.push(`font-size:${s.fontSize}pt`);
  if (s.color) parts.push(`color:${s.color}`);
  if (s.highlight) parts.push(`background-color:${s.highlight}`);
  if (s.bold) parts.push('font-weight:700');
  if (s.italic) parts.push('font-style:italic');
  if (s.underline) parts.push('text-decoration:underline');
  if (s.align) parts.push(`text-align:${s.align}`);
  if (s.lineHeight) parts.push(`line-height:${s.lineHeight}`);
  return parts.join(';');
}

/**
 * @process bandStyleCss
 * Steps: Serialize a BandStyle (background/border/padding/columns) to CSS.
 * Inputs: BandStyle.
 * Outputs: CSS string (empty when no style properties are set). Deterministic.
 * Idempotency: REQUIRED (pure).
 */
export function bandStyleCss(s: BandStyle): string {
  const parts: string[] = [];
  if (s.background) parts.push(`background:${s.background}`);
  if (s.border) parts.push(`border:${borderCss(s.border)}`);
  if (s.padding) {
    const p = s.padding;
    parts.push(`padding:${p.top ?? 0}pt ${p.right ?? 0}pt ${p.bottom ?? 0}pt ${p.left ?? 0}pt`);
  }
  if (s.columns && s.columns > 1) parts.push(`columns:${s.columns}`);
  return parts.join(';');
}
